import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { PageHead } from "../GtaxShell";
import { DepositPanel } from "./DepositPanel";
import { KycPanel } from "./KycPanel";
import { AgreementCopy } from "../AgreementCopy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/gtax`;
const LABEL = { pending: "Identity check pending", approved: "Approved", rejected: "Not approved" };

const saved = () => {
  try { return JSON.parse(sessionStorage.getItem("gtax_member")) || {}; } catch { return {}; }
};

const TIER_NAME = { standard: "Standard", vip1: "VIP I", vip2: "VIP II", vip3: "VIP III" };

const Referrals = ({ data }) => (
  <div className="pt-8 border-t border-[color:var(--line)]" data-testid="gtax-status-referrals">
    <div className="flex flex-wrap justify-between gap-6">
      <div><div className="gx-eyebrow">Your referrals</div><p className="mt-2 text-[color:var(--mute)]">Members who joined through your invitation link.</p></div>
      <div className="flex gap-10">
        <div><div className="text-xs uppercase tracking-widest text-[color:var(--mute)]">Members</div><div className="gx-serif text-3xl" data-testid="gtax-referrals-count">{data.members.length}</div></div>
        <div><div className="text-xs uppercase tracking-widest text-[color:var(--mute)]">Introducer rewards</div><div className="gx-serif text-3xl tabular-nums" data-testid="gtax-referrals-earned">${data.earned.toLocaleString("en-US", { minimumFractionDigits: 2 })}</div></div>
      </div>
    </div>
    {data.members.length > 0 && (
      <table className="w-full mt-6 text-sm" data-testid="gtax-referrals-table"><tbody>
        {data.members.map((m, i) => (
          <tr key={i} className="border-b border-[color:var(--line)]"><td>{m.name}</td><td>{TIER_NAME[m.tier]}</td><td className="text-[color:var(--mute)]">Joined {m.joined}</td><td className="text-right capitalize">{m.status}</td></tr>
        ))}
      </tbody></table>
    )}
    <p className="mt-4 text-xs text-[color:var(--mute)]">Introducer rewards come only from realised profit, never from any member's allocation.</p>
  </div>
);

const InviteLink = ({ reference }) => {
  const link = `${window.location.origin}/access?ref=${reference}`;
  const copy = () => navigator.clipboard.writeText(link).then(() => toast.success("Invitation link copied."));
  return (
    <div className="pt-8 border-t border-[color:var(--line)]" data-testid="gtax-status-invite">
      <div className="gx-eyebrow">Your personal invitation link</div>
      <p className="mt-2 text-[color:var(--mute)]">Share it with people you know. Members who join through it are recorded as referred by you.</p>
      <div className="mt-4 font-mono text-base break-all" data-testid="gtax-status-invite-link">{link}</div>
      <button onClick={copy} className="gx-ghost mt-4 inline-flex items-center gap-2" data-testid="gtax-status-invite-copy"><Copy size={16} /> Copy link</button>
    </div>
  );
};

export default function GtaxStatus() {
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get("ref") || saved().reference || "");
  const [email, setEmail] = useState(params.get("email") || saved().email || "");
  const [member, setMember] = useState(null);
  const [error, setError] = useState("");

  const lookup = async (e) => {
    e?.preventDefault();
    setError("");
    try {
      const { data } = await axios.get(`${API}/status`, { params: { reference: ref.trim(), email: email.trim() } });
      setMember({ ...data, email: email.trim().toLowerCase() });
      sessionStorage.setItem("gtax_member", JSON.stringify({ reference: data.reference, email: email.trim().toLowerCase() }));
    } catch (err) {
      setMember(null);
      setError(err.response?.data?.detail || "Could not find your membership.");
    }
  };

  useEffect(() => {
    const init = window.Telegram?.WebApp?.initData;
    if (init && !params.get("ref")) {
      axios.post(`${API}/tg/me`, { init_data: init }).then(({ data }) => { setRef(data.reference); setEmail(data.email); setMember(data); }).catch(() => {});
    } else if (ref && email) lookup();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="gtax-status-page">
      <PageHead eyebrow="Member status" title="Your membership." sub="Enter your reference and email to see your verification status and settlement details." />
      <section className="pb-28">
        <div className="gx-wrap max-w-4xl space-y-8">
          <form onSubmit={lookup} className="gx-card p-8 grid md:grid-cols-[1fr_1fr_auto] gap-6 items-end" data-testid="gtax-status-form">
            <div><label className="block text-xs tracking-widest uppercase text-[color:var(--mute)]">Reference</label>
              <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="GTX-XXXXXXXX" className="w-full py-3 text-lg font-mono" data-testid="gtax-status-ref" /></div>
            <div><label className="block text-xs tracking-widest uppercase text-[color:var(--mute)]">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full py-3 text-lg" data-testid="gtax-status-email" /></div>
            <button className="gx-btn" data-testid="gtax-status-submit">Check</button>
          </form>
          {error && <p className="text-[#A0523F]" data-testid="gtax-status-error">{error}</p>}
          {member && (
            <div className="gx-card p-8 md:p-12 space-y-8" data-testid="gtax-status-result">
              <div className="flex flex-wrap justify-between gap-4">
                <div><div className="gx-eyebrow">{member.full_name}</div><div className="gx-serif text-3xl mt-2">{member.reference}</div></div>
                <div className="gx-serif text-2xl gx-gold" data-testid="gtax-status-kyc">{LABEL[member.kyc_status]}</div>
              </div>
              {member.kyc_status === "approved" && member.channel !== "telegram" && <DepositPanel record={member} address={member.treasury_address} compact />}
              {member.kyc_status === "approved" && member.channel === "telegram" && (
                <div className="gx-card p-6" data-testid="gtax-status-tg-deposit">Your settlement details are in the GTA X Telegram chat. Send <b>/deposit</b> in the bot to choose your network and see the treasury address, and <b>/txid</b> to record a transfer.</div>
              )}
              {member.kyc_status === "approved" && <InviteLink reference={member.reference} />}
              {member.referrals && <Referrals data={member.referrals} />}
              {member.kyc_status === "approved" && <AgreementCopy record={member} />}
              {member.kyc_status === "pending" && <KycPanel record={member} />}
              {member.kyc_status === "rejected" && <p className="text-[color:var(--mute)]" data-testid="gtax-status-rejected">We could not approve your identity verification. Please contact us through the Contact page.</p>}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
